import { ApiClient } from "client/base";
import { delay } from "utils/promise";
import { tableFromArrayBuffer } from "@/utils/arrow";
const POLL_INTERVAL_MS = 1000;
export class CloudCompletionClient extends ApiClient {
    createCompletion(request, options) {
        const params = new URLSearchParams("");
        if (options?.maxCacheAge !== undefined) {
            params.append("cache_cutoff", ((Date.now() - options?.maxCacheAge) / 1000).toString());
        }
        if (options?.cacheSimilarityScoreCutoff !== undefined) {
            params.append("similarity_score_cutoff", options?.cacheSimilarityScoreCutoff.toString());
        }
        return this.base
            .post(`v1/completions?${params.toString()}`, request)
            .then((result) => {
            // if (result.status == "INPROGRESS") {
            //     return delay(POLL_INTERVAL_MS).then(
            //         () => this.pollAsyncProcess(result.id, (id: string) => this.getCompletion<T>(id))
            //     )
            // }
            return result;
        })
            .then((x) => ({
            ...x,
            is_cached: x.is_cached == true,
            updated_date: x.updated_date ?? new Date().toISOString()
        }));
    }
    generateContent(data) {
        return this.base.post(`v1/completions`, data);
    }
    awaitContentValidationResult(id) {
        return this.pollAsyncProcess(id, (id) => this.getContentValidationResult(id));
    }
    getContentValidationResult(id) {
        return this.base.get(`v1/completions/${id}/validation_result`);
    }
    getGeneratedContent(id) {
        return this.base.get(`v1/completions/${id}/generated_content`);
    }
    getGeneratedContentResults(id) {
        return this.base.get(`v1/completions/${id}/results`);
    }
    getErrorNotebookUrl(id) {
        return this.base.get(`v1/notebooks/chat_completion/${id}`);
    }
    pollAsyncProcess(id, fetch) {
        return fetch(id).then((result) => {
            if (result.status == "INPROGRESS") {
                return delay(POLL_INTERVAL_MS).then(() => this.pollAsyncProcess(id, fetch));
            }
            return result;
        });
    }
    getCompletion(id) {
        return this.base.get(`v1/completions/${id}`);
    }
    async getFinishedCompletion(id) {
        let result;
        while (true) {
            result = await this.getCompletion(id);
            if (result?.status === "COMPLETE" || result?.status === "ERROR") {
                break;
            }
            await delay(POLL_INTERVAL_MS);
        }
        return {
            ...result,
            is_cached: result.is_cached == true,
            updated_date: result.updated_date ?? new Date().toISOString()
        };
    }
    getDetectedValues(datasetId, prompt) {
        return this.base.post(`v1/completions/datasets/${datasetId}/detect_values`, {
            prompt
        });
    }
    addFineTuningData(id, records) {
        return this.base.post(`v1/fine-tuning-data/${id}`, records);
    }
    getFineTuningData(id) {
        return this.base.get(`v1/fine-tuning-data/${id}`);
    }
    createFineTuningDataset(id, data) {
        return this.base.post(`v1/fine-tuning-data/${id}`, data);
    }
    deleteFineTuningDataset(id) {
        return this.base.delete(`v1/fine-tuning-data/${id}`);
    }
    addDocumentUrls(id, urls) {
        return this.base
            .post(`v1/completions/corpus/${id}/reindex`, { urls: urls })
            .then((result) => {
            if (result.status == "INPROGRESS") {
                return delay(5000)
                    .then(() => this.pollAsyncProcess(result.id, (id) => this.getCompletion(id)))
                    .then(() => Promise.resolve());
            }
            return Promise.resolve();
        });
    }
    reindexCorpus(id, options) {
        return this.base
            .post(`v1/completions/corpus/${id}/reindex`, options)
            .then(() => delay(5000))
            .then(() => this.pollCorpusReindex(id));
    }
    pollCorpusReindex(id) {
        return this.getCorpus(id).then((result) => {
            if (result.metadata?.status != "INPROGRESS") {
                return result;
            }
            return delay(POLL_INTERVAL_MS).then(() => this.pollCorpusReindex(id));
        });
    }
    getCorpus(id) {
        return this.base.get(`v1/completions/corpus/${id}`);
    }
    getCorpusDocumentList(id) {
        return this.base.get(`v1/completions/corpus/${id}/files`).then((result) => {
            const docList = result?.file_names ?? [];
            if (!docList?.length) {
                return Promise.resolve([]);
            }
            return Promise.resolve(docList);
        });
    }
    getKPIAnalysis(id, offset, updateProgress) {
        const SKIP_PAGINATION = false;
        const LIMIT = 3000;
        const CHART_LIMIT = 45000;
        const isFirstCall = !offset;
        const queryParams = new URLSearchParams("");
        const retOffset = offset || 0;
        const retLimit = LIMIT;
        queryParams.append("limit", String(retLimit));
        if (retOffset) {
            queryParams.append("offset", String(retOffset));
        }
        let retQuery = `?${queryParams.toString()}`;
        if (SKIP_PAGINATION) {
            retQuery = "";
        }
        if (isFirstCall && updateProgress) {
            updateProgress(0);
        }
        return this.base
            .makeRequestRaw("GET", `v1/completions/kpi/${id}${retQuery}`)
            .then((response) => {
            const contentType = response.headers.get("Content-Type");
            if (contentType == "application/json") {
                return response.json();
            }
            else if (contentType != "application/vnd.apache.arrow.stream") {
                throw Error(`unexpected content type: ${contentType}`);
            }
            return response.arrayBuffer().then((data) => {
                const table = tableFromArrayBuffer(data);
                const rows = table.toArray();
                const output = new Array(rows.length);
                if (rows.length == 0) {
                    return output;
                }
                const keys = Object.keys(rows[0]).filter((k) => k != "kpi_label" && k != "type");
                for (let i = 0; i < rows.length; i++) {
                    output[i] = rows[i].toJSON();
                    // eslint-disable-next-line @typescript-eslint/prefer-for-of
                    for (let j = 0; j < keys.length; j++) {
                        output[i][keys[j]] = output[i][keys[j]].toJSON();
                        if (typeof output[i][keys[j]][0] == "bigint") {
                            output[i][keys[j]] = output[i][keys[j]].map(Number);
                        }
                    }
                }
                return output;
            });
        })
            .then((result) => {
            if (!result?.length) {
                return Promise.resolve([]);
            }
            if (result.length < LIMIT || SKIP_PAGINATION) {
                if (updateProgress) {
                    updateProgress(retOffset + result.length);
                }
                return Promise.resolve(result);
            }
            const newOffset = (retOffset || 0) + LIMIT;
            if (newOffset >= CHART_LIMIT) {
                return Promise.resolve(result);
            }
            if (updateProgress) {
                updateProgress(newOffset);
            }
            return this.getKPIAnalysis(id, newOffset, updateProgress)
                .then((nResult) => {
                if (isFirstCall && updateProgress) {
                    updateProgress();
                }
                return result.concat(nResult);
            })
                .catch(() => {
                if (isFirstCall && updateProgress) {
                    updateProgress();
                }
                return result;
            });
        });
    }
    getArtifactsWithDetails(filter) {
        const searchParams = new URLSearchParams("");
        for (const [key, value] of Object.entries(filter)) {
            searchParams.append(key, String(value));
        }
        return this.base.get(`v1/completions/artifacts?${searchParams.toString()}`);
    }
    getModelGarden() {
        return this.base.get(`v1/completions/model_garden_endpoints`);
    }
    createFineTunedModel(create) {
        return this.base.post(`v1/fine-tuned-models`, create).then((result) => {
            return result;
        });
    }
    getFineTunedModel(id) {
        return this.base.get(`v1/fine-tuned-models/${id}`);
    }
    cancelFineTuning(id) {
        return this.base.delete(`v1/fine-tuned-models/${id}`);
    }
    savePreferredRequest(executionId, preferredExecutionId) {
        return this.base.post(`v1/completions/${executionId}/preferred_requests/${preferredExecutionId}`);
    }
    getPreferredRequests(request, options) {
        const params = new URLSearchParams("");
        if (options?.reasoningSimilarityScoreCutoff !== undefined) {
            params.append("similarity_score_cutoff", options?.reasoningSimilarityScoreCutoff.toString());
        }
        return this.base.post(`v1/completions/preferred_requests?${params.toString()}`, request);
    }
    resetMultiSessionMemory() {
        return this.base.delete(`v1/completions/multi_session_memory/reset`);
    }
    validate(executionId, content) {
        return this.base.post(`v1/completions/validate`, {
            execution_ids: [executionId],
            content: content
        });
    }
    getTools() {
        return this.base.get(`v1/tools/configs`);
    }
    addTool(request) {
        return this.base.post(`v1/tools/config`, request);
    }
    deleteTool(toolId) {
        return this.base.delete(`v1/tools/${toolId}/config`);
    }
}
